import {
  ArrowUp,
  Bot,
  Brain,
  ChevronRight,
  CircleAlert,
  Loader2,
  MessageSquare,
  Paperclip,
  Plus,
  Square,
  Wrench,
  X,
} from "lucide-react"
import * as React from "react"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { toast } from "sonner"

import { EmptyState } from "@/components/empty-state"
import { JsonOrText, JsonViewer } from "@/components/json-viewer"
import { Markdown } from "@/components/markdown"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { Textarea } from "@/components/ui/textarea"
import { useLocale, useT } from "@/i18n/provider"
import { ApiError, api, streamRun, toastApiError, toastErrorWithContact } from "@/lib/api"
import type {
  Agent,
  Conversation,
  Message,
  RunEvent,
  ToolStartedEvent,
} from "@/lib/types"
import { cn, formatDuration, formatRelative } from "@/lib/utils"

/** A tool call as the UI knows it: announced, then resolved, possibly out of order. */
interface LiveToolCall {
  id: string
  name: string
  connection: string
  mcpName: string
  args: Record<string, unknown> | null
  done: boolean
  isError: boolean
  errorKind: string | null
  durationMs: number | null
  preview: string | null
}

const ACCEPTED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif"]
const MAX_IMAGE_BYTES = 10 * 1024 * 1024
const MAX_IMAGES = 4

export function ChatPage() {
  const navigate = useNavigate()
  const t = useT()
  const { locale } = useLocale()
  const { conversationId } = useParams()
  const [searchParams] = useSearchParams()

  const [agents, setAgents] = React.useState<Agent[]>([])
  const [agentId, setAgentId] = React.useState(searchParams.get("agent") ?? "")
  const [conversations, setConversations] = React.useState<Conversation[]>([])
  const [messages, setMessages] = React.useState<Message[]>([])
  const [input, setInput] = React.useState("")
  const [loading, setLoading] = React.useState(true)
  const [pending, setPending] = React.useState<{ id: string; url: string }[]>([])
  const [attaching, setAttaching] = React.useState(false)
  const fileInputRef = React.useRef<HTMLInputElement>(null)

  const [streamingText, setStreamingText] = React.useState("")
  const [streamingReasoning, setStreamingReasoning] = React.useState("")
  const [liveCalls, setLiveCalls] = React.useState<LiveToolCall[]>([])
  const [runId, setRunId] = React.useState<string | null>(null)
  const [busy, setBusy] = React.useState(false)
  const cancelRef = React.useRef<(() => void) | null>(null)
  const bottomRef = React.useRef<HTMLDivElement>(null)
  const inputRef = React.useRef<HTMLTextAreaElement>(null)

  const agent = agents.find((entry) => entry.id === agentId)
  const canSend = input.trim() !== "" && agentId !== "" && !busy

  // Grow with the text; the max-height class caps it and scrolls beyond that. Done in
  // JS because CSS field-sizing is not supported everywhere yet.
  React.useLayoutEffect(() => {
    const el = inputRef.current
    if (!el) return
    el.style.height = "auto"
    el.style.height = `${el.scrollHeight}px`
  }, [input])

  // Ready to type on arrival. Skipped on touch screens, where focusing pops the keyboard.
  React.useEffect(() => {
    if (window.matchMedia("(pointer: fine)").matches) inputRef.current?.focus()
  }, [agentId, conversationId, loading])

  React.useEffect(() => {
    api
      .listAgents()
      .then((list) => {
        setAgents(list)
        setAgentId((current) => current || list[0]?.id || "")
      })
      .catch((error) =>
        toast.error(error instanceof ApiError ? error.message : t("common.loadAgentsFailed")),
      )
      .finally(() => setLoading(false))
  }, [t])

  React.useEffect(() => {
    if (!agentId) return
    api.listConversations(agentId).then(setConversations).catch(() => setConversations([]))
  }, [agentId])

  React.useEffect(() => {
    if (!conversationId) {
      setMessages([])
      return
    }
    api
      .messages(conversationId)
      .then(setMessages)
      .catch(() => toast.error(t("chat.toasts.loadConversationFailed")))
    api
      .getConversation(conversationId)
      .then((conversation) => setAgentId(conversation.agent_id))
      .catch(() => undefined)
  }, [conversationId, t])

  React.useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages, streamingText, liveCalls])

  // Revoke any outstanding local preview URLs if the page is left mid-compose.
  const pendingRef = React.useRef(pending)
  pendingRef.current = pending
  React.useEffect(() => {
    return () => {
      pendingRef.current.forEach((item) => URL.revokeObjectURL(item.url))
    }
  }, [])

  async function attachFiles(files: FileList | File[]) {
    const images = Array.from(files).filter((file) => ACCEPTED_IMAGE_TYPES.includes(file.type))
    if (images.length === 0) return

    let count = pending.length
    setAttaching(true)
    try {
      for (const file of images) {
        if (count >= MAX_IMAGES) {
          toast.error(t("chat.tooManyImages"))
          break
        }
        if (file.size > MAX_IMAGE_BYTES) {
          toast.error(t("chat.imageTooLarge"))
          continue
        }
        try {
          const uploaded = await api.uploadAttachment(file)
          const url = URL.createObjectURL(file)
          setPending((current) => [...current, { id: uploaded.id, url }])
          count += 1
        } catch (error) {
          toastApiError(error, t("chat.attachmentUploadFailed"))
        }
      }
    } finally {
      setAttaching(false)
    }
  }

  function removePending(id: string) {
    setPending((current) => {
      const target = current.find((item) => item.id === id)
      if (target) URL.revokeObjectURL(target.url)
      return current.filter((item) => item.id !== id)
    })
  }

  function resetStreamingState() {
    setStreamingText("")
    setStreamingReasoning("")
    setLiveCalls([])
    setRunId(null)
    cancelRef.current = null
  }

  function handleEvent(event: RunEvent, conversation: { id: string | null }) {
    switch (event.type) {
      case "run.started":
        conversation.id = event.conversation_id
        break
      case "text.delta":
        setStreamingText((current) => current + event.delta)
        break
      case "reasoning.delta":
        setStreamingReasoning((current) => current + event.delta)
        break
      case "tool.started": {
        const started = event as ToolStartedEvent
        setLiveCalls((current) => [
          ...current,
          {
            id: started.tool_call_id,
            name: started.llm_tool_name,
            connection: started.connection_slug,
            mcpName: started.mcp_tool_name,
            args: started.arguments,
            done: false,
            isError: false,
            errorKind: null,
            durationMs: null,
            preview: null,
          },
        ])
        break
      }
      case "tool.completed":
        // Resolves by id, because calls finish out of order.
        setLiveCalls((current) =>
          current.map((call) =>
            call.id === event.tool_call_id
              ? {
                  ...call,
                  done: true,
                  isError: event.is_error,
                  errorKind: event.error_kind,
                  durationMs: event.duration_ms,
                  preview: event.result_preview,
                }
              : call,
          ),
        )
        break
      case "message.completed":
        // Authoritative version of what was just streamed.
        setStreamingText("")
        setStreamingReasoning("")
        break
      case "run.failed":
        toastErrorWithContact(event.message)
        break
      default:
        break
    }
  }

  async function send(event: React.FormEvent) {
    event.preventDefault()
    const text = input.trim()
    if (!text || !agentId || busy) return

    const attachmentIds = pending.map((item) => item.id)

    setInput("")
    setBusy(true)
    resetStreamingState()
    // Clicking Send moves focus to the button, which is swapped for Stop.
    inputRef.current?.focus()

    // Shown immediately; replaced by the committed row when the run finishes.
    const optimistic: Message = {
      id: `pending-${Date.now()}`,
      seq: (messages.at(-1)?.seq ?? 0) + 1,
      role: "user",
      content: text,
      tool_calls: null,
      tool_call_id: null,
      tool_name: null,
      reasoning: null,
      finish_reason: null,
      iteration: null,
      usage: null,
      content_truncated: false,
      run_id: null,
      created_at: new Date().toISOString(),
      attachments: [],
    }
    setMessages((current) => [...current, optimistic])

    const target = { id: conversationId ?? null }
    const path = conversationId
      ? `/conversations/${conversationId}/runs`
      : `/agents/${agentId}/runs`

    const body: Record<string, unknown> = { text, client_message_id: crypto.randomUUID() }
    if (attachmentIds.length > 0) body.attachment_ids = attachmentIds

    try {
      const handle = await streamRun(path, body, {
        onEvent: (item) => handleEvent(item, target),
        onError: () => toast.error(t("chat.toasts.connectionDropped")),
        onRunId: setRunId,
      })
      cancelRef.current = handle.cancel
      // The run started successfully - clear the composer's attachments (revoking their
      // local preview URLs) so a new message can be composed while this one streams.
      setPending((current) => {
        current.forEach((item) => URL.revokeObjectURL(item.url))
        return []
      })
      await handle.done

      // Reload from the database rather than trusting the stream: the runtime commits
      // before emitting, so this is always the authoritative view.
      if (target.id) {
        const fresh = await api.messages(target.id)
        setMessages(fresh)
        if (!conversationId) navigate(`/chat/${target.id}`, { replace: true })
        api.listConversations(agentId).then(setConversations).catch(() => undefined)
      }
    } catch (error) {
      setMessages((current) => current.filter((message) => message.id !== optimistic.id))
      // The input stays editable during a run, so never clobber a newer draft.
      setInput((current) => (current.trim() ? current : text))
      toastApiError(error, t("chat.toasts.startRunFailed"))
    } finally {
      setBusy(false)
      resetStreamingState()
    }
  }

  async function stop() {
    if (runId) {
      try {
        await api.cancelRun(runId)
      } catch {
        /* the local abort below still stops the stream */
      }
    }
    cancelRef.current?.()
    inputRef.current?.focus()
  }

  if (loading) return <div className="text-muted-foreground p-6 text-sm">{t("common.loading")}</div>

  if (agents.length === 0) {
    return (
      <div className="p-6">
        <EmptyState
          icon={Bot}
          title={t("chat.noAgents.title")}
          description={t("chat.noAgents.description")}
          actionLabel={t("common.createAgent")}
          onAction={() => navigate("/agents/new")}
        />
        <p className="text-muted-foreground mt-4 text-center text-sm">
          {t("chat.noAgents.newHere")}{" "}
          <button className="text-foreground underline" onClick={() => navigate("/guide")}>
            {t("chat.noAgents.readGuide")}
          </button>
        </p>
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0">
      <div className="hidden w-64 shrink-0 flex-col border-r lg:flex">
        <div className="space-y-2 p-3">
          <Select
            value={agentId}
            onValueChange={(value) => {
              setAgentId(value)
              navigate("/chat")
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder={t("chat.pickAgent")} />
            </SelectTrigger>
            <SelectContent>
              {agents.map((entry) => (
                <SelectItem key={entry.id} value={entry.id}>
                  {entry.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" className="w-full" onClick={() => navigate("/chat")}>
            <Plus className="size-4" />
            {t("chat.newConversation")}
          </Button>
        </div>
        <Separator />
        <div className="min-h-0 flex-1 overflow-y-auto p-2">
          {conversations.length === 0 ? (
            <p className="text-muted-foreground p-3 text-xs">{t("chat.noConversations")}</p>
          ) : (
            conversations.map((conversation) => (
              <button
                key={conversation.id}
                onClick={() => navigate(`/chat/${conversation.id}`)}
                className={cn(
                  "hover:bg-accent/60 w-full rounded-md px-3 py-2 text-left text-sm transition-colors",
                  conversation.id === conversationId && "bg-accent",
                )}
              >
                <div className="truncate font-medium">
                  {conversation.title || t("chat.untitledConversation")}
                </div>
                <div className="text-muted-foreground text-xs">
                  {formatRelative(conversation.last_message_at ?? conversation.created_at, locale)}
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-center justify-between gap-3 border-b px-6 py-3">
          <div className="min-w-0">
            <div className="truncate font-medium">{agent?.name ?? t("chat.agentFallback")}</div>
            <div className="text-muted-foreground text-xs">
              {agent?.model} · {t("common.toolCount", { count: agent?.tool_count ?? 0 })}
            </div>
          </div>
          <div className="lg:hidden">
            <Select value={agentId} onValueChange={setAgentId}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {agents.map((entry) => (
                  <SelectItem key={entry.id} value={entry.id}>
                    {entry.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-6">
          <div className="mx-auto w-full max-w-3xl space-y-4">
            {messages.length === 0 && !busy ? (
              <EmptyState
                icon={MessageSquare}
                title={t("chat.emptyMessages.title")}
                description={
                  agent?.tool_count
                    ? t("chat.emptyMessages.withTools", { name: agent.name, count: agent.tool_count })
                    : t("chat.emptyMessages.noTools", {
                        name: agent?.name ?? t("chat.thisAgentFallback"),
                      })
                }
              />
            ) : (
              messages.map((message) => <MessageBubble key={message.id} message={message} />)
            )}

            {streamingReasoning && <ReasoningBlock text={streamingReasoning} live />}
            {liveCalls.map((call) => (
              <ToolCallCard key={call.id} call={call} />
            ))}
            {streamingText && (
              <div className="flex gap-3">
                <Avatar role="assistant" />
                {/* Same bubble as the committed reply, so nothing jumps when it lands. */}
                <div className="bg-muted min-w-0 max-w-[85%] rounded-lg px-3 py-2">
                  <Markdown text={streamingText} />
                </div>
              </div>
            )}
            {busy && !streamingText && liveCalls.length === 0 && (
              <div className="text-muted-foreground flex items-center gap-2 text-sm">
                <Loader2 className="size-4 animate-spin" />
                {t("chat.assistantThinking")}
              </div>
            )}
            <div ref={bottomRef} />
          </div>
        </div>

        <form onSubmit={send} className="px-4 pt-2 pb-4">
          <div className="mx-auto w-full max-w-3xl">
            {pending.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-2">
                {pending.map((item) => (
                  <div key={item.id} className="group relative">
                    <img
                      src={item.url}
                      alt=""
                      className="border-input size-16 rounded-md border object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => removePending(item.id)}
                      aria-label={t("chat.removeImage")}
                      title={t("chat.removeImage")}
                      className="bg-background text-foreground absolute -top-1.5 -right-1.5 flex size-5 items-center justify-center rounded-full border shadow-xs"
                    >
                      <X className="size-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div
              className="border-input bg-muted/40 focus-within:border-ring focus-within:ring-ring/50 flex cursor-text items-end gap-2 rounded-2xl border py-2 pr-2 pl-4 shadow-xs transition-[color,box-shadow] focus-within:ring-[3px]"
              onClick={() => inputRef.current?.focus()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_IMAGE_TYPES.join(",")}
                multiple
                className="hidden"
                onChange={(event) => {
                  const files = event.target.files
                  if (files && files.length > 0) void attachFiles(files)
                  event.target.value = ""
                }}
              />
              <Button
                type="button"
                size="icon"
                variant="ghost"
                onClick={() => fileInputRef.current?.click()}
                disabled={attaching || pending.length >= MAX_IMAGES}
                aria-label={t("chat.attach")}
                title={t("chat.attach")}
                className="text-muted-foreground size-8 shrink-0 rounded-full"
              >
                <Paperclip className="size-4" />
              </Button>
              <Textarea
                ref={inputRef}
                value={input}
                onChange={(event) => setInput(event.target.value)}
                onKeyDown={(event) => {
                  // keyCode 229: Safari reports the Enter that commits an IME
                  // composition with isComposing already false.
                  const composing = event.nativeEvent.isComposing || event.keyCode === 229
                  if (event.key !== "Enter" || event.shiftKey || composing) return
                  event.preventDefault()
                  if (canSend) void send(event)
                }}
                onPaste={(event) => {
                  const files = Array.from(event.clipboardData?.files ?? []).filter((file) =>
                    file.type.startsWith("image/"),
                  )
                  if (files.length > 0) void attachFiles(files)
                }}
                placeholder={t("chat.messagePlaceholder", {
                  name: agent?.name ?? t("chat.fallbackAgentNameLower"),
                })}
                aria-label={t("chat.messageAriaLabel", {
                  name: agent?.name ?? t("chat.fallbackAgentNameLower"),
                })}
                rows={1}
                className="max-h-52 min-h-0 flex-1 resize-none overflow-y-auto rounded-none border-0 bg-transparent px-0 py-1.5 shadow-none focus-visible:ring-0 dark:bg-transparent"
              />
              {busy ? (
                <Button
                  type="button"
                  size="icon"
                  variant="secondary"
                  onClick={stop}
                  aria-label={t("chat.stop")}
                  title={t("chat.stop")}
                  className="size-8 rounded-full"
                >
                  <Square className="size-3 fill-current" />
                </Button>
              ) : (
                <Button
                  type="submit"
                  size="icon"
                  disabled={!canSend}
                  aria-label={t("chat.send")}
                  title={t("chat.send")}
                  className="size-8 rounded-full disabled:opacity-30"
                >
                  <ArrowUp className="size-4" />
                </Button>
              )}
            </div>
            <p className="text-muted-foreground mt-2 text-center text-xs">
              {busy ? t("chat.hint.busy") : t("chat.hint.idle")}
            </p>
          </div>
        </form>
      </div>
    </div>
  )
}

function Avatar({ role }: { role: string }) {
  const t = useT()
  return (
    <div
      className={cn(
        "flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-medium",
        role === "user" ? "bg-primary text-primary-foreground" : "bg-muted",
      )}
    >
      {role === "user" ? t("chat.you") : <Bot className="size-4" />}
    </div>
  )
}

function MessageBubble({ message }: { message: Message }) {
  const t = useT()

  if (message.role === "tool") {
    return (
      <div className="ml-10 rounded-lg border px-3 py-2">
        <div className="text-muted-foreground mb-1 flex items-center gap-2 text-xs">
          <Wrench className="size-3.5" />
          {message.tool_name}
          {message.content_truncated && (
            <Badge variant="warning">{t("chat.truncated")}</Badge>
          )}
        </div>
        <JsonOrText text={message.content ?? ""} className="text-muted-foreground max-h-48" />
      </div>
    )
  }

  const isUser = message.role === "user"
  return (
    <div className="space-y-2">
      {message.reasoning && <ReasoningBlock text={message.reasoning} />}
      <div className={cn("flex gap-3", isUser && "flex-row-reverse")}>
        <Avatar role={message.role} />
        <div
          className={cn(
            "min-w-0 max-w-[85%] rounded-lg px-3 py-2 text-sm",
            // What the user typed is shown verbatim; only model output is markdown.
            isUser ? "bg-primary text-primary-foreground whitespace-pre-wrap" : "bg-muted",
          )}
        >
          {message.attachments.length > 0 && (
            <div className="mb-2 flex flex-wrap gap-2">
              {message.attachments.map((attachment) => (
                <AttachmentImage key={attachment.id} id={attachment.id} />
              ))}
            </div>
          )}
          {!message.content ? (
            <span className="text-muted-foreground italic">
              {t("chat.emptyAssistantMessage")}
            </span>
          ) : isUser ? (
            message.content
          ) : (
            <Markdown text={message.content} />
          )}
        </div>
      </div>
      {message.tool_calls?.length ? (
        <div className="ml-10 flex flex-wrap gap-2">
          {message.tool_calls.map((call) => (
            <Badge key={call.id} variant="outline" className="payload">
              <Wrench className="size-3" />
              {call.function.name}
            </Badge>
          ))}
        </div>
      ) : null}
    </div>
  )
}

/** Resolves an attachment's bytes into a local blob URL on mount, and revokes it again
 *  on unmount - the id alone (no signed URL) is not directly usable as an <img src>. */
function AttachmentImage({ id }: { id: string }) {
  const [url, setUrl] = React.useState<string | null>(null)
  React.useEffect(() => {
    let revoked: string | null = null
    let active = true
    api
      .attachmentBlobUrl(id)
      .then((resolved) => {
        if (active) {
          setUrl(resolved)
          revoked = resolved
        } else {
          URL.revokeObjectURL(resolved)
        }
      })
      .catch(() => undefined)
    return () => {
      active = false
      if (revoked) URL.revokeObjectURL(revoked)
    }
  }, [id])
  return url ? <img src={url} alt="" className="max-h-64 rounded-md" /> : null
}

function ReasoningBlock({ text, live = false }: { text: string; live?: boolean }) {
  const t = useT()
  return (
    <Collapsible className="ml-10">
      <CollapsibleTrigger asChild>
        <Button variant="ghost" size="sm" className="text-muted-foreground h-7 px-2">
          <Brain className="size-3.5" />
          {live ? t("chat.reasoning.live") : t("chat.reasoning.label")}
          <ChevronRight className="size-3.5" />
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <pre className="payload text-muted-foreground bg-muted/50 mt-1 max-h-56 overflow-auto rounded-md p-3 whitespace-pre-wrap">
          {text}
        </pre>
      </CollapsibleContent>
    </Collapsible>
  )
}

function ToolCallCard({ call }: { call: LiveToolCall }) {
  return (
    <div
      className={cn(
        "ml-10 rounded-lg border px-3 py-2",
        call.isError && "border-destructive/40 bg-destructive/5",
      )}
    >
      <div className="flex flex-wrap items-center gap-2 text-xs">
        {call.done ? (
          call.isError ? (
            <CircleAlert className="text-destructive size-3.5" />
          ) : (
            <Wrench className="size-3.5" />
          )
        ) : (
          <Loader2 className="size-3.5 animate-spin" />
        )}
        <span className="payload font-medium">{call.mcpName}</span>
        <Badge variant="outline">{call.connection}</Badge>
        {call.done && call.durationMs != null && (
          <span className="text-muted-foreground">{formatDuration(call.durationMs)}</span>
        )}
        {call.errorKind && <Badge variant="destructive">{call.errorKind}</Badge>}
      </div>
      {call.args && Object.keys(call.args).length > 0 && (
        <JsonViewer value={call.args} defaultExpandDepth={2} className="mt-2 max-h-32" />
      )}
      {call.preview && (
        <p className="text-muted-foreground mt-2 line-clamp-2 text-xs">{call.preview}</p>
      )}
    </div>
  )
}
